import { useCallback, useEffect, useState } from "react";
import { PhoneCall, PhoneMissed, X } from "lucide-react";
import api from "@/api/client";
import { useVoice } from "@/contexts/VoiceCallContext";

/**
 * Shows a red bubble on tracking/driver pages when a call has been missed.
 * - Fetches the last unseen missed call for this order on mount.
 * - Also reacts live to `call.missed` WS events forwarded via useVoice(): the
 *   voice call hook listens on user:<self.id> and re-emits into VoiceContext.
 *   We piggyback by re-fetching on window focus + when a fresh missed event
 *   arrives (see VoiceContext.lastMissed).
 * - Dismissable per-session (localStorage) so the same call doesn't nag.
 */
export default function MissedCallBanner({ orderId, onCallBack }) {
  const voice = useVoice();
  const [missed, setMissed] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("missed_dismissed") || "[]"); }
    catch { return []; }
  });

  const refresh = useCallback(async () => {
    if (!orderId) return;
    try {
      const { data } = await api.get(`/orders/${orderId}/missed-calls`);
      const dismissed = new Set(dismissedIds);
      const latest = (data || []).find((m) => !dismissed.has(m.call_id));
      setMissed(latest || null);
    } catch { setMissed(null); }
  }, [orderId, dismissedIds]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch when a fresh call.missed event arrives (voice hook receives it).
  useEffect(() => {
    if (voice.lastMissed && voice.lastMissed.order_id === orderId) refresh();
  }, [voice.lastMissed, orderId, refresh]);

  // Poll on visibility change (covers cases where the tab was hidden during the call).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  if (!missed) return null;

  const dismiss = () => {
    const next = [...dismissedIds, missed.call_id];
    setDismissedIds(next);
    try { sessionStorage.setItem("missed_dismissed", JSON.stringify(next.slice(-50))); } catch { /* ignore */ }
    setMissed(null);
  };

  const callBack = () => {
    if (onCallBack) onCallBack();
    else voice.startOutgoing(orderId, { name: missed.caller_name });
    dismiss();
  };

  const who = missed.caller_role === "driver" ? "السائق" : "الزبون";
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl bg-red-50 border border-red-200 mb-3"
      data-testid="missed-call-banner"
    >
      <div className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0 animate-pulse-dot">
        <PhoneMissed size={18}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-red-700">مكالمة فائتة</div>
        <div className="text-xs text-red-600 truncate">
          من {who} {missed.caller_name ? `— ${missed.caller_name}` : ""}
        </div>
      </div>
      <button
        onClick={callBack}
        className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-primary-brand text-white text-xs font-black hover:opacity-90 shrink-0"
        data-testid="missed-call-back-btn"
      >
        <PhoneCall size={12}/> معاودة
      </button>
      <button
        onClick={dismiss}
        className="p-1.5 rounded-full text-red-500 hover:bg-red-100 shrink-0"
        aria-label="إخفاء"
        data-testid="missed-call-dismiss-btn"
      >
        <X size={16}/>
      </button>
    </div>
  );
}
